import {Inject, Injectable} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {catchError, concat, map, Observable, of} from 'rxjs';
import {UserService} from '../user.service';
import {User} from '../../models/user.model';
import {DefaultValuesService} from '../defaultValues.service';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {UrlService} from '../url.service';

@Injectable()
export class InitializationAndConfigurationService {
    private readonly BASE_URL: string;
    private domain: string;
    private hostName: string;
    private hostPort: any;
    private isInMaintenanceMode: boolean = false;
    private applicationConfiguration: any;
    private toDoCategoryConfiguration: any;
    private externalContactsConfiguration: any;
    private loggedInUser: User;
    private documentsEnabled: boolean = false;

    private availableLinks = null;
    private activeModules = null;
    private passwordValidations = null;
    private passwordValidationsHighPrivilege = null;
    private themeName = null;
    private isSimplifiedEdit: boolean = false;
    private simplifiedEditEnabled: boolean = false;

    constructor(private userService: UserService,
                private defaultValuesService: DefaultValuesService,
                private http: HttpClient,
                @Inject(DOCUMENT) private doc: any,
                private urlService: UrlService) {
        this.BASE_URL = this.urlService.getBackendUrl() + 'data/admin/configuration';
    }

    initializeApplication(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.domain = /:\/\/([^\/]+)/.exec(this.doc.location.href)[1].split('.')[0];

            this.hostName = this.doc.location.hostname;
            this.hostPort = this.doc.location.port;

            if (history.scrollRestoration) {
                history.scrollRestoration = 'manual';
            }

            // if (token && token.domain) {
            //     this.domain = token.domain;
            // }

            const task1 = this.userService.getLoggedInUser().pipe(
                map(user => {
                    this.loggedInUser = new User(user);
                })
            );

            const task2 = this.getConfiguration().pipe(
                map(configuration => {
                    if (configuration) {
                        this.applicationConfiguration = configuration;

                        this.activeModules = configuration.activeModulesList;
                        this.availableLinks = JSON.parse(configuration.availableLinks);
                        this.isSimplifiedEdit = configuration.simplifiedDataEntryModeDefault;
                        this.simplifiedEditEnabled = configuration.simplifiedDataEntryModeDefault;
                        this.passwordValidations = [];
                        const validations = configuration.validationRegexes;
                        validations.forEach((validation) => {
                            if (validation.length === 2) {
                                this.passwordValidations.push({
                                    validation: validation[0],
                                    message: validation[1].trim(),
                                    regex: new RegExp(validation[0])
                                });
                            }
                        });

                        this.passwordValidationsHighPrivilege = [];
                        const validationsHighPrivilege = configuration.validationRegexesHighPrivilege;
                        validationsHighPrivilege.forEach((validation) => {
                            if (validation.length === 2) {
                                this.passwordValidationsHighPrivilege.push({
                                    validation: validation[0],
                                    message: validation[1].trim(),
                                    regex: new RegExp(validation[0])
                                });
                            }
                        });

                        // Set the favicon based on configuration.
                        this.themeName = configuration.themeName;
                        if (!this.themeName) {
                            this.themeName = 'oa_default';
                        }

                        this.toDoCategoryConfiguration = this.defaultValuesService.toDoCategoryConfiguration;
                        const pipeSplits = configuration.inboxConfigDefinition.split('|');
                        pipeSplits.forEach((pipeSplit) => {
                            const colonSplits = pipeSplit.split(':');
                            this.toDoCategoryConfiguration.forEach((toDoCategory) => {
                                if (toDoCategory.toDoType === colonSplits[0]) {
                                    if (toDoCategory.permission) {
                                        if ((this.loggedInUser.isSuperUser) ||
                                            (this.userService.hasPermission(toDoCategory.permission, this.loggedInUser))) {
                                            toDoCategory.display = true;
                                        }
                                    } else {
                                        toDoCategory.display = true;
                                    }
                                }
                            });
                        });

                        const toDoCommon = configuration.toDoCommon;
                        const toDoCommonJSON = JSON.parse(toDoCommon);
                        toDoCommonJSON.configs.forEach((c) => {
                            c.types.forEach((type) => {
                                this.toDoCategoryConfiguration.forEach((toDoCategory) => {
                                    if (toDoCategory.toDoType === type) {
                                        if (toDoCategory.permission) {
                                            if ((this.loggedInUser.isSuperUser) ||
                                                (this.userService.hasPermission(toDoCategory.permission, this.loggedInUser))) {
                                                toDoCategory.display = true;
                                            }
                                        } else {
                                            toDoCategory.display = true;
                                        }
                                    }
                                });
                            });
                        });

                        if (configuration.externalContactsConfigurationJSON) {
                            this.externalContactsConfiguration = JSON.parse(configuration.externalContactsConfigurationJSON);
                        }

                        this.documentsEnabled = configuration.documentsEnabled;

                        if (this.applicationConfiguration.instrumentTypeClientAgeFilterNew) {
                            // Preliminary implementation of JSON config format still
                            // needs changes in the backend
                            const filter = this.applicationConfiguration.instrumentTypeClientAgeFilterNew;
                            this.applicationConfiguration.instrumentTypeClientAgeFilterMap = JSON.parse(filter);
                        } else if (this.applicationConfiguration.instrumentTypeClientAgeFilter !== 'null') {
                            // Build the filters map
                            this.applicationConfiguration.instrumentTypeClientAgeFilterMap = {};
                            this.applicationConfiguration.instrumentTypeClientAgeFilter.split('|').forEach((filter) => {
                                    const filterComponents = filter.split(':');
                                    this.applicationConfiguration.instrumentTypeClientAgeFilterMap[filterComponents[0]]
                                        = filterComponents[1];
                                }
                            );
                        }

                        if (this.applicationConfiguration.instrumentTypeClientOUFilter !== 'null') {
                            // Build the filters map
                            this.applicationConfiguration.instrumentTypeClientOUFilterMap = {};
                            this.applicationConfiguration.instrumentTypeClientOUFilter.split('|').forEach((filter) => {
                                const filterComponents = filter.split(':');
                                this.applicationConfiguration.instrumentTypeClientOUFilterMap[filterComponents[0]] = filterComponents[1] + ',' + filterComponents[2];
                            });
                        }
                    }
                }),
                catchError((error: any) => of(error)));

            concat(...[task1, task2]).subscribe({
                complete: () => {
                    resolve({});
                },
                error: (error) => {
                    console.log(error);
                    // If it's a 401, the interceptor is redirecting to CAS.
                    // Don't resolve the promise - let the app hang while the redirect happens.
                    // This prevents other components from initializing and making more API calls.
                    if (error instanceof HttpErrorResponse && error.status === 401) {
                        console.log('401 received - waiting for CAS redirect');
                        return; // Don't resolve, let redirect happen
                    }
                    this.isInMaintenanceMode = true;
                    resolve({});
                }
            });
        });
    }

    getDomain() {
        return this.domain;
    }

    getHostName() {
        return this.hostName;
    }

    getHostPort() {
        return this.hostPort;
    }

    getAvailableLinks() {
        return this.availableLinks;
    }

    getApplicationConfiguration() {
        return this.applicationConfiguration;
    }

    getLoggedInUser() {
        return this.loggedInUser;
    }

    getActiveModules() {
        return this.activeModules;
    }

    getPasswordValidations(isHighPrivilege: boolean) {
        if (isHighPrivilege) {
            return this.passwordValidationsHighPrivilege;
        } else {
            return this.passwordValidations;
        }
    }

    getThemeName() {
        return this.themeName;
    }

    getIsInMaintenanceMode() {
        return this.isInMaintenanceMode;
    }

    getToDoCategoryConfiguration() {
        return this.toDoCategoryConfiguration;
    }

    getExternalContactConfiguration() {
        return this.externalContactsConfiguration;
    }

    isDocumentsEnabled() {
        return this.documentsEnabled;
    }

    isSimplifiedEditEnabled() {
        return this.simplifiedEditEnabled;
    }

    getIsSimplifiedEdit() {
        return this.isSimplifiedEdit;
    }

    userHasPermission(permission, user) {
        if (user == null || user === 'undefined') {
            user = this.loggedInUser;
        }

        if (user != null && !user.isAnonymous) {
            if (user.isSuperAdmin) {
                return true;
            }
            let ret = false;
            user.permissions.forEach((per) => {
                if (per === permission) {
                    ret = true;
                    return;
                }
            });
            return ret;
        }

        return false;
    }

    userIsSuperAdmin() {
        if (this.loggedInUser != null) {
            return this.loggedInUser.isSuperAdmin;
        }
        return false;
    }

    userIsOuResponsible(ou) {
        if (ou == null) {
            return false;
        }
        if (this.loggedInUser != null) {
            if (this.userIsSuperAdmin()) {
                return true;
            }
            let ret = false;
            this.loggedInUser.ouResponsibilities.forEach((resp) => {
                if (ou.id === resp.id) {
                    ret = true;
                    return;
                }
            });
            return ret;
        }

        return false;
    }

    userIsClientResponsible(client) {
        if (client == null) {
            return false;
        }
        if (this.loggedInUser != null) {
            if (this.userIsSuperAdmin()) {
                return true;
            }
            let ret = false;
            this.loggedInUser.clientIdsResponsibilities.forEach((resp) => {
                if (client.id === resp) {
                    ret = true;
                    return;
                }
            });
            return ret;
        }

        return false;
    }

    canAccessApplication(application) {
        if (!this.loggedInUser) {
            return false;
        }

        if (this.loggedInUser.isSuperAdmin) {
            return true;
        }

        let ret = false;
        this.loggedInUser.roles.forEach((role) => {
            switch (application) {
                case 'data_entry':
                    if (this.loggedInUser.hasDataEntryAccess) {
                        ret = true;
                        return;
                    }
                    break;
                case 'executive_dashboard':
                    if (this.loggedInUser.hasDashboardAccess) {
                        ret = true;
                        return;
                    }
                    break;
                case 'admin':
                    if (this.loggedInUser.hasAdminAccess) {
                        ret = true;
                        return;
                    }
                    break;
            }
        });
        return ret;
    }

    hasPermission(permission) {
        if (!this.loggedInUser) {
            return false;
        }

        if (this.loggedInUser.isSuperAdmin) {
            return true;
        }
        let ret = false;
        this.loggedInUser.permissions.forEach((value, key) => {
            if (value === permission) {
                ret = true;
                return;
            }
        });
        return ret;
    }

    isModuleActive(moduleName) {
        let res = false;
        this.activeModules.forEach((module) => {
            if (moduleName === module) {
                res = true;
            }
        });
        return res;
    }

    getConfiguration(): Observable<any> {
        return this.http.get(this.BASE_URL);
    }

    applyInstrumentTypeClientAgeFilterNew(instrument, clientAgeInMonths) {
        const range = this.applicationConfiguration.instrumentTypeClientAgeFilterMap[instrument.instrumentType];

        if (!range) {
            return true;
        }

        return (range[0] || 0) <= clientAgeInMonths && clientAgeInMonths <= (range[1] || Infinity);
    }

    applyInstrumentTypeClientAgeFilter(instrument, clientAgeInMonths, ignoreMissingFilter) {
        // Work-around due to default arguments not being supported by minifier
        ignoreMissingFilter = typeof ignoreMissingFilter === 'undefined' ? false : ignoreMissingFilter;

        const filter = this.applicationConfiguration.instrumentTypeClientAgeFilterMap[instrument.instrumentType];

        if (!filter) {
            return !ignoreMissingFilter;
        }
        // filter uses this specific variable name in the eval()

        try {
            const evaluateFunction = new Function(
                'clientMonths',
                'return ' + filter);
            return evaluateFunction(
                clientAgeInMonths);
        } catch (e) {
            console.error(e);
            console.error(filter);
        }
    }

    applyInstrumentTypeClientOUFilter(instrumentType: string, orgUnits: string[]): boolean {
        const ouList = this.applicationConfiguration.instrumentTypeClientOUFilterMap[instrumentType];
        if (!ouList) {
            return true;
        }

        const filterList = ouList.split(',');
        const mode = filterList[0].toLowerCase();
        const ouSet = new Set(filterList.slice(1)); // skip mode keyword

        let ret: boolean;

        if (mode === 'include') {
            // Allow if any orgUnit is in the include list
            ret = orgUnits.some(orgUnit => ouSet.has(orgUnit));
        } else if (mode === 'exclude') {
            // Reject only if all orgUnits are in the exclude list
            ret = !orgUnits.every(orgUnit => ouSet.has(orgUnit));
        } else {
            // Invalid configuration — default to allowing
            ret = true;
        }

        return ret;
    }
}
